import { observable } from "mobx";
import { defaultTo, isNumber, toString } from "lodash";
import { IsNotEmpty, IsNumberString, ValidateIf } from "class-validator";
import {
  BonlivaCareInvoiceInput,
  BookingFinanceEditor_ItemFragment,
  ContractType,
  LegalPersonType,
  UpdateBookingFinanceInput
} from "@graphql";
import locale from "@bonliva-crm/locale";

export class BookingFinance implements UpdateBookingFinanceInput {
  static FromGql(data: BookingFinanceEditor_ItemFragment) {
    const item = new BookingFinance();

    item.BonlivaCareInvoice =
      data.CostCenter?.Organisation === "BonlivaCare"
        ? {
            invoiceNumber: data.Invoices[0]?.invoiceNumber ?? "",
            invoiceTotal: data.GroupInvoiceTotal ?? 0
          }
        : null;

    item.AccommodationCostStr = toString(defaultTo(data.costs?.accommodation, 0));
    item.AccommodationCost = data.costs?.accommodation;
    item.AdditionalWork = data.AdditionalWork;
    item.AdditionalWorkNote = data.AdditionalWorkNote ?? "";
    item.ContractType = data.ContractType;
    item.CustomerFine = toString(data.CustomerFine);
    item.AdministrativeFee = toString(data.AdministrativeFee);
    item.EmployeeSalary = data.costs?.employeeSalary;
    item.EmployeeSalaryStr = toString(data.costs?.employeeSalary);
    item.IsConsultantPayed = data.IsConsultantPayed;
    item.IsSickPayed = data.isSickPayed;
    item.TimeReportHasSickTime = data.timeReportHasSickTime;
    item.Note = data.Note;
    item.PatientId = data.PatientId;
    item.SubcontractorInvoiceTotal = data.costs?.invoiceTotal;
    item.SubcontractorInvoiceTotalStr = toString(defaultTo(data.costs?.invoiceTotal, 0));
    item.TravelCost = data.costs?.travel;
    item.TravelCostStr = toString(defaultTo(data.costs?.travel, 0));
    item.GroupInvoiceTotal = data.GroupInvoiceTotal;
    item.GroupInvoiceTotalStr =
      isNumber(data.GroupInvoiceTotal) &&
      parseFloat(data.GroupInvoiceTotal.toString()) !== 0 &&
      data.GroupInvoiceTotal.toString() !== "0"
        ? data.GroupInvoiceTotal.toString()
        : "";

    item.Invoices = data.Invoices.map((x) => x.invoiceNumber);
    item.LegalPersonType = data.ConsultantSnapshot.LegalPersonType;
    item.BusinessName = data.ConsultantSnapshot.BusinessName || "";
    item.BusinessSocialSecurityNo =
      data.ConsultantSnapshot.BusinessSocialSecurityNo || "";
    item.SickPayTotal = data.costs?.sickPayTotal;
    item.SickPayTotalStr = toString(defaultTo(data.costs?.sickPayTotal, 0));

    return item;
  }

  @observable
  AccommodationCost: number | null = null;

  @ValidateIf((o) => o.IsConsultantPayed === true)
  @IsNumberString()
  @observable
  AccommodationCostStr: string = "";

  @observable
  AdditionalWork: boolean = false;

  @observable
  AdditionalWorkNote: string = "";

  @observable
  BusinessName: string = "";

  @observable
  BusinessSocialSecurityNo: string = "";

  @ValidateIf((o) => o.LegalPersonType === LegalPersonType.Employee)
  @IsNotEmpty({ message: locale["Type of contract is a requirement for employees"] })
  @observable
  ContractType: ContractType | null = null;

  @observable
  CustomerFine: string = "";

  @observable
  AdministrativeFee: string = "";

  @observable
  DeletedSubcontractorDocumentIds: string[] = [];

  @observable
  EmployeeSalary: number | null = null;

  // @ValidateIf(
  //   (o: BookingFinance) =>
  //     o.LegalPersonType === LegalPersonType.Employee && !!o.IsConsultantPayed
  // )
  // @NumberStringMin(1, {
  //   message: locale.bookingFinance.EmployeeSalary
  // })
  @observable
  EmployeeSalaryStr: string = "";

  @observable
  GroupInvoiceTotal: number | null = null;

  @observable
  GroupInvoiceTotalStr: string = "";

  @observable
  Invoices: string[] = [];

  @observable
  IsConsultantPayed: boolean = false;

  @observable
  LegalPersonType: LegalPersonType | null = null;

  @observable
  NewSubcontractorDocumentIds: string[] = [];

  @observable
  Note: string = "";

  @observable
  PatientId: string = "";

  @observable
  SubcontractorInvoiceTotal: number | null = null;

  // @ValidateIf(
  //   (o: BookingFinance) =>
  //     o.LegalPersonType === LegalPersonType.Subcontractor && !!o.IsConsultantPayed
  // )
  // @NumberStringMin(1, {
  //   message: locale.bookingFinance.SubcontractorInvoiceTotal
  // })
  @observable
  SubcontractorInvoiceTotalStr: string = "";

  @observable
  TravelCost: number | null = null;

  @ValidateIf((o) => o.IsConsultantPayed === true)
  @IsNumberString()
  @observable
  TravelCostStr: string = "";

  @observable
  UpdateConsultantWithSnapshotData: boolean = false;

  @observable
  ChangedKeys = [];

  @observable
  BonlivaCareInvoice: BonlivaCareInvoiceInput;

  @observable
  IsSickPayed: boolean = false;

  @observable
  TimeReportHasSickTime: boolean = false;

  @observable
  SickPayTotal: number | null = null;

  @ValidateIf((o) => o.IsSickPayed === true)
  @IsNumberString()
  @observable
  SickPayTotalStr: string = "";
}
